/**
 * Stone Investment Search Bar
 * 
 * Elegant search input with Stone Investment's luxury aesthetic:
 * - Greyscale colour palette
 * - Montserrat typography
 * - Subtle hover and focus states
 */

'use client';

import { Search, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export interface StoneSearchBarProps {
  /**
   * Current search value
   */
  value?: string;
  /**
   * Callback when search value changes
   */
  onChange: (value: string) => void;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Clear search aria-label
   */
  clearSearchLabel?: string;
  /**
   * Debounce delay in ms
   */
  debounceMs?: number;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * StoneSearchBar - Elegant search input with Stone Investment styling
 */
export function StoneSearchBar({
  value = '',
  onChange,
  placeholder = 'Search properties...',
  clearSearchLabel = 'Clear search',
  debounceMs = 300,
  className = '',
  size = 'md',
}: StoneSearchBarProps) {
  const [localValue, setLocalValue] = useState(value);
  const debounceRef = useRef<NodeJS.Timeout>(undefined);
  const lastEmittedRef = useRef(value);
  
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setLocalValue(value);
      lastEmittedRef.current = value;
    }
  }, [value]);
  
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
  
  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      lastEmittedRef.current = newValue;
      onChange(newValue);
    }, debounceMs);
  };
  
  const handleClear = () => {
    setLocalValue('');
    lastEmittedRef.current = '';
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onChange('');
  };
  
  const sizeClasses = {
    sm: 'h-10 text-sm',
    md: 'h-12 text-base',
    lg: 'h-14 text-lg',
  };
  
  const iconSizes = {
    sm: 16,
    md: 18,
    lg: 20,
  };
  
  return (
    <div className={`relative ${className}`}>
      {/* Search Icon */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <Search size={iconSizes[size]} className="text-stone-400" strokeWidth={1.5} />
      </div>
      
      {/* Input */}
      <input
        type="text"
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className={`
          w-full ${sizeClasses[size]} pl-12 pr-12
          border border-stone-300 bg-white
          font-sans text-stone-900 placeholder:text-stone-400
          focus:outline-none focus:border-stone-500 focus:ring-1 focus:ring-stone-200
          hover:border-stone-400 transition-colors
        `}
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      />
      
      {/* Clear Button */}
      {localValue && (
        <button
          onClick={handleClear}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
          aria-label={clearSearchLabel}
        >
          <X size={iconSizes[size]} strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
}

export default StoneSearchBar;
